import Vue from 'vue'
import Router from 'vue-router';
import { auth, remoteConfig } from './firebase';
import store from './store';
import { EventBus } from '@/eventBus';
import { readSignupInProgress, clearSignupInProgress } from './lib/signupSession';

const Admin = () => import(/* webpackChunkName: "admin" */ './views/Admin.vue');
const EmailAction = () =>
	import(/* webpackChunkName: "action" */ './views/EmailAction.vue');
const Trial = () => import(/* webpackChunkName: "trial" */ './views/Trial.vue');
const Login = () => import(/* webpackChunkName: "login" */ './views/Login.vue');
const ForgotPassword = () => import(/* webpackChunkName: "forgotPassword" */ './views/ForgotPassword.vue');
const ResetPassword = () => import(/* webpackChunkName: "resetPassword" */ './views/ResetPasswordFlow.vue');
const Create = () =>
	import(/* webpackChunkName: "create" */ './views/Agreements/Create.vue');
const DownloadWrapper = () =>
	import(
		/* webpackChunkName: "DownloadWrapper" */ './views/DownloadWrapper.vue'
	);
const DownloadSummary = () =>
	import(
		/* webpackChunkName: "DownloadSummary" */ './views/DownloadSummary.vue'
	);
const Agreement = () =>
	import(/* webpackChunkName: "agreement" */ './views/Agreement.vue');
const Agreements = () =>
	import(
		/* webpackChunkName: "agreementsHome" */ './views/Agreements/Home.vue'
	);
const Profile = () =>
	import(/* webpackChunkName: "profile" */ './views/Profile.vue');
const Subscription = () =>
	import(/* webpackChunkName: "subscription" */ './views/Subscription.vue');
const ChildSupportCalculations = () =>
	import(
		/* webpackChunkName: "childSupport" */ './views/ChildSupportCalculationsWrapper.vue'
	);

const ClioHome = () => import(/* webpackChunkName: "ClioHome" */ './views/ClioHome.vue');
const PostLogin = () => import('./views/PostLogin.vue');

const authentication = () => import(/* webpackChunkName: "ClioHome" */ './views/authentication.vue');
const UserRole = () =>
	import(/* webpackChunkName: "create" */ './views/UserRole/Roles.vue');

function checkAuthStatus() {
	return new Promise((resolve, reject) => {
		try {
			auth.onAuthStateChanged((user) => {
				resolve(user);
			});
		} catch {
			reject('auth status fail');
		}
	});
}

async function requireAdmin(to, from, next) {
	const { claims } = await auth.currentUser.getIdTokenResult();

	if (
		process.env.NODE_ENV !== 'production' ||
		remoteConfig.getValue('staging').asBoolean()
	) {
		next();
		return;
	}

	if (!auth.currentUser || !store.getters.loading || !claims.admin) {
		console.log('....')
		if (to.params && to.params.id) {
			next({
				name: 'agreement',
				params: { id: to.params.id },
			});
		} else {
			console.log('+++')
			next({
				name: 'postLogin',
			});
		}
	}
	next();
}

function lastAuthPath() {
	return localStorage.getItem('lastRole') === 'party' ? '/couple/auth' : '/auth';
}

function requireAuth(to, from, next) {
	if (!auth.currentUser && !store.getters.loading) {
		next({ path: lastAuthPath() });
	}
	next();
}

function clientCheck(to, from, next) {
	// if (localStorage.getItem("clientUserAgreement")) {
	// 	next({
	// 		name: 'login',
	// 	});
	// }
	next();
}

function isAuthed(to, from, next) {
	if (auth.currentUser) {
		next({
			name: 'home',
		});
	}
	next();
}

function beforeEnterUserRole(to, from, next) {
	// Allows access to users role page only for specified user emails
	const emails= ['test-divii@mailinator.com', 'ramya.r@aot-technologies.com', 'neetha.john@aot-technologies.com', 'rebecca@divii.ca', 'bronwen@divii.ca', 'tech@divii.ca'];
	if(!emails.includes(auth?.currentUser?.email)) {
		EventBus.$emit(
			'show-toast', 
			{ title: 'Permission Denied!', message: 'You don\'t have permission for users role page', variant: 'danger' }
		);
		next({ name: 'agreementsHome' });
	}
	next();
}

export const AUTH_ROUTES = ['auth', 'coupleAuth', 'login'];

function abandonedSignup(to) {
	if (!auth.currentUser || AUTH_ROUTES.includes(to.name)) {
		return null;
	}
	const marker = readSignupInProgress();
	return marker && marker.uid === auth.currentUser.uid ? marker : null;
}

const ROUTES_ALLOWED_WHEN_UNPAID = ['subscription', 'auth', 'coupleAuth', 'login'];

const SESSION_RESOLVE_TIMEOUT = 15000;

function waitForSession() {
	if (store.state.sessionResolved) {
		return Promise.resolve();
	}
	return new Promise((resolve) => {
		const timer = setTimeout(() => store.commit('setSessionResolved', true), SESSION_RESOLVE_TIMEOUT);
		const unwatch = store.watch(
			(state) => state.sessionResolved,
			() => {
				clearTimeout(timer);
				unwatch();
				resolve();
			}
		);
	});
}

export function isPendingPayment() {
	if (store.state.userProfile?.requiresPayment !== true) {
		return false;
	}
	// Escape hatch if clearing the flag after checkout failed.
	if (store.state.payments?.length > 0) {
		return false;
	}
	return !!auth.currentUser;
}

function isBlockedPendingPayment(to) {
	return isPendingPayment() && !ROUTES_ALLOWED_WHEN_UNPAID.includes(to.name);
}

const router = new Router({
	mode: 'history',
	base: process.env.BASE_URL,
	routes: [

		{
			name: 'childSupport',
			path: '/child-support',
			component: ChildSupportCalculations,
			beforeEnter: clientCheck,
			meta: {
				requiresAuth: false,
				noRedirect: true,
			},
		},
		{
			name: 'home',
			path: '/',
			// redirect: localStorage.getItem('clientUserAgreement') ? {
			// 	name: 'clientUserAgreement',
			// 	params: { id: localStorage.getItem('clientUserAgreement') },
			// } : { name: 'agreementsHome' },
			redirect:{name :'agreementsHome'},
			meta: { posthog_category: 'home_page' },
		},
		{
			name: 'trial',
			path: '/trial',
			component: Trial,
			beforeEnter: isAuthed,
			meta: {
				requiresAuth: false,
				posthog_category: 'trial'
			},
			redirect: to => ({ name :'auth', query: { ...to.query, page: 1, auth:1 } }) 
		},
		{
			name: 'trialClientUser',
			path: '/trial/:id',
			component: Trial,
			beforeEnter: isAuthed,
			meta: {
				requiresAuth: false,
			},
			redirect: to => ({ name :'auth', query: { ...to.query, page: 1, auth:1 } })
		},
		{
			name: 'login',
			path: '/login',
			component: Login,
			beforeEnter: isAuthed,
			meta: {
				requiresAuth: false,
				posthog_category: 'login'
			},
			redirect: to => ({ name :'auth', query: { ...to.query, page: 1, auth:0 } })
		},
		{
			name: 'loginClientUser',
			path: '/login/:id',
			component: Login,
			beforeEnter: isAuthed,
			meta: {
				requiresAuth: false,
				posthog_category: 'client_user_login'
			},
			redirect: to => ({ name :'auth', query: { ...to.query, page: 1, auth:0 } })
		},
		{
			name: 'action',
			path: '/action',
			props: true,
			meta: {
				requiresAuth: false,
			},
			component: EmailAction,
		},
		{
			name: 'createAgreement',
			path: '/agreement/create',
			// beforeEnter: requireAuth,
			beforeEnter(to, from, next) {
				requireAuth
				// if (localStorage.getItem("clientUserAgreement")) {
				// 	next({
				// 		name: 'login',
				// 	});
				// }
				next();
			},
			component: Create,
			meta: {
				requiresAuth: true,
				posthog_category: 'agreement_creation'
			},
		},
		{
			name: 'preview',
			path: '/agreement/:id/preview',
			// beforeEnter: requireAuth,
			beforeEnter(to, from, next) {
				requireAuth
				// if (localStorage.getItem("clientUserAgreement")) {
				// 	next({
				// 		name: 'login',
				// 	});
				// }
				next();
			},
			component: DownloadWrapper,
			props: true,
			meta: {
				requiresAuth: true,
				og_category: 'agreement_preview'
			},
		},
		{
			
			name: 'summary',
			path: '/agreement/:id/summary',
			beforeEnter: requireAuth,
			component: DownloadSummary,
			props: true,
			meta: {
				requiresAuth: true,
			},
		},
		{
			name: 'admin',
			path: '/agreement/:id/admin',
			beforeEnter: requireAdmin,
			component: Admin,
			meta: {
				requiresAuth: true,
			},
		},
		{
			name: 'agreement',
			path: '/agreement/:id',
			// beforeEnter: requireAuth,
			beforeEnter(to, from, next) {
				requireAuth
				// if (localStorage.getItem("clientUserAgreement")) {
				// 	next({
				// 		name: 'login',
				// 	});
				// }
				next();
			},
			component: Agreement,
			props: true,
			meta: {
				requiresAuth: true,
				posthog_category: 'agreement_view'
			},
		},
		{
			name: 'clientUserAgreement',
			path: '/clientUserAgreement/:id',
			beforeEnter: requireAuth,
			component: Agreement,
			props: true,
			meta: {
				requiresAuth: true,
				posthog_category: 'agreement_view'
			},
		},
		{
			name: 'agreementsHome',
			path: '/agreements/',
			beforeEnter: requireAuth,
			component: Agreements,
			// beforeEnter(to, from, next) {
			// 	requireAuth
			// 	if (localStorage.getItem("clientUserAgreement")) {
			// 		next({
			// 			name: 'login',
			// 		});
			// 	}
			// 	next();
			// },
			meta: {
				requiresAuth: true,
				posthog_category: 'agreements_list'
			},
		},
		{
			name: 'profile',
			path: '/profile/',
			// beforeEnter: requireAuth,
			beforeEnter(to, from, next) {
				requireAuth
				// if (localStorage.getItem("clientUserAgreement")) {
				// 	next({
				// 		name: 'login',
				// 	});
				// }
				next();
			},
			component: Profile,
			meta: {
				requiresAuth: true,
			},
		},
		{
			name: 'subscription',
			path: '/subscription/',
			// beforeEnter: requireAuth,
			beforeEnter(to, from, next) {
				requireAuth
				// if (localStorage.getItem("clientUserAgreement")) {
				// 	next({
				// 		name: 'login',
				// 	});
				// }
				next();
			},
			component: Subscription,
			meta: {
				requiresAuth: true,
			},
		},
		{
			name: 'forgotpassword',
			path: '/forgotpassword',
			component: ForgotPassword,
			// beforeEnter(to, from, next) {
			//   const user = firebase.auth().currentUser;
			//   if (user) {
			// 	next({ name: "dashboard" });
			//   } else {
			// 	next();
			//   }
			// },
		  },
		  {
			name: 'resetpassword',
			path: '/resetpassword',
			component: ResetPassword,
			meta: {
				requiresAuth: true,
			},
		  },
		  {
			name: 'identity_callback',
			path: '/identity_callback',
			redirect: { name: 'clioHome' },
		  },
		  {
			name: 'callback',
			path: '/callback',
			redirect: { name: 'clioHome' },
		  },
		  {
			name: 'clioHome',
			path: '/clioHome',
			component: ClioHome,
		  },
		  {
			name: 'auth',
			path: '/auth',
			component: authentication,
			beforeEnter: isAuthed,
			props: { userType: 'lawyer' },
			meta: {
				requiresAuth: false,
				noRedirect: true,
				posthog_category: 'lawyer_authentication'
			},
		  },
		  {
			name: 'coupleAuth',
			path: '/couple/auth',
			component: authentication,
			beforeEnter: isAuthed,
			props: { userType: 'party' },
			meta: {
			  requiresAuth: false,
			  noRedirect: true,
			  posthog_category: 'couple_authentication'
			},
		  },
		  {
			name: 'postLogin',
			path: '/postLogin',
			component: PostLogin,
		  },
		  {
			name: 'userrole',
			path: '/userrole',
			component: UserRole,
			beforeEnter: beforeEnterUserRole,
			meta: {
				requiresAuth: true,
			},
		  },
		{
			path: '/form-components',
			component: () =>
				import(
					/* webpackChunkName: "formComponents" */ './views/FormComponents.vue'
				),
		},
	],
});

// navigation guard to check for logged in users
router.beforeEach(async (to, from, next) => {
	if (store.state.isLoggingOut) {
        return next();
    }
	await checkAuthStatus();
	Vue.prototype.$posthog.capture('$pageview', {
		$current_url: to.fullPath,
		page_name: to.name || 'unknown',
		category: to.meta.posthog_category || 'unknown',
		user_id: store.state.user?.uid || null,
		role: store.state.posthogRole || '',
		timestamp: new Date().toISOString(),
		from_page: from.name || 'none',
		from_url: from.fullPath || 'none',
	});
	if (auth.currentUser && !store.state.sessionResolved) {
		await waitForSession();
	}

	const abandoned = abandonedSignup(to);
	if (abandoned) {
		clearSignupInProgress();
		await store.dispatch('logout');
		store.commit('setLoggingOut', false);
		EventBus.$emit('show-toast', {
			title: 'Finish creating your account',
			message: 'Your account setup was not completed. Please sign up again to finish.',
			variant: 'warning',
		});
		return next({ name: abandoned.authRoute, query: { page: 1, auth: 1 } });
	}

	if (isBlockedPendingPayment(to)) {
		return next({ name: 'subscription' });
	}

	// console.log('*****')
	if (
		to.matched.some((record) => record.meta.requiresAuth) &&
		!auth.currentUser &&
		!store.getters.loading
	) {
		next({ path: lastAuthPath(), replace: true });
	} else if (
		to.matched.some((record) => !record.meta.requiresAuth) &&
		to.matched.some((record) => !record.meta.noRedirect) &&
		auth.currentUser
	) {
		// next({ path: '/' });
		// console.log('cccccccc')
		// if(to.name != 'postLogin')
		// return next({ path: '/postLogin' });
		if(to.matched[0].name == 'clioHome')
		next();
		else
		next({ path: '/' });
	}
	// console.log('to.name============>',to.name)

	// if(to.name == 'postLogin'){
	// 	next();
	// }
	// if(to.name == 'trialClientUser' && to.params && to.params.id){
	// 	// localStorage.setItem('clientUserAgreement', to.params.id);
	// 	console.log('trialClientUser')
	// 	next({ path: '/trial', replace: true });
	// }
	// if(to.name == 'loginClientUser' && to.params && to.params.id){
	// 	localStorage.setItem('clientUserAgreement', to.params.id);
	// 	console.log('loginClientUser')
	// 	next({ path: '/login', replace: true });
	// }
	// if(to.name == 'agreementsHome'){
			// if(localStorage.getItem('clientUserAgreement')) {
			// 	sessionStorage.setItem('activeTab','builder')
			// 	sessionStorage.setItem('activeIndex',0)
			// 	next({
			// 		name: 'clientUserAgreement',
			// 		params: { id: localStorage.getItem('clientUserAgreement') }
			// 	});
			// }
	// }
	// if(to.name == 'clientUserAgreement'){
	// 	sessionStorage.setItem('activeTab','builder')
	// 	sessionStorage.setItem('activeIndex',0)
	// }
	if(from?.name == 'agreement'){
		sessionStorage.setItem('activeIndex',0);
		sessionStorage.setItem('activeTab','builder');
	}
	next();
});

router.afterEach((to) => {
	if (isBlockedPendingPayment(to)) {
		router.push({ name: 'subscription' }).catch(() => {});
	}
});

store.watch(
	(state) => state.userProfile?.requiresPayment === true,
	(requiresPayment) => {
		if (requiresPayment && isBlockedPendingPayment(router.currentRoute)) {
			router.push({ name: 'subscription' }).catch(() => {});
		} else if (!requiresPayment && router.currentRoute.name === 'subscription') {
			router.push({ name: 'agreementsHome' }).catch(() => {});
		}
	}
);

export default router;
